/**
 * Application route definitions.
 * 
 * Centralizes all route configuration to keep App.tsx focused on
 * providers and shell layout.
 */

import React, { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIsMobileActive } from '../hooks/useIsMobileActive';
import { ProcurementIndexRedirect } from '../pages/Procurement/routing/ProcurementRedirect';
import { useAuth } from '../context/AuthContext';
import { isAdminRoutePath } from './routeCoverage';
import {
  BomCreateRoute,
  BomEditRoute,
  ItemCreateRoute,
  ItemDuplicateRoute,
  ItemEditRoute,
  KitCreateRoute,
  KitEditRoute,
  PurchaseOrderCreateRoute,
  PurchaseOrderEditRoute,
  PurchaseRequisitionCreateRoute,
  PurchaseRequisitionEditRoute,
} from '../pages/Procurement/routing/DetailRoutes';

// Import all lazy-loaded pages
import {
  DashboardPage,
  ToolsLayout,
  ApiTester,
  UsageAllocator,
  WireSpecsTool,
  WireUtilityTool,
  ConduitFillTool,
  DevPage,
  UsersPage,
  PlaygroundPage,
  ProcurementPage,
  EstimateDetailRoute,
  EstimatesHomeRoute,
  DecisionCenterPage,
  FilesPage,
  QuickBooksPage,
  PayrollPage,
  ActivitiesPage,
  FeedPage,
  ReportsPage,
  ReportDetailPage,
  ResourcesPage,
  SOPsPage,
  SOPDetailPage,
  GuidesPage,
  EnphasePage,
  GoogleAdsAnalyticsPage,
  DealsPage,
  DealDetailPage,
  WebProposalPreviewPage,
  ProjectsPage,
  ProjectCreatePage,
  ProjectDetailPage,
  HecoPage,
  DesignsPage,
  PermitsPage,
  ServiceMaintenancePage,
  ServiceOrderDetailPage,
  ContactsPage,
  ContactDetailPage,
  TasksPage,
  TaskDetailPage,
  CalendarPage,
  CalendarEventDetailPage,
  EmployeesPage,
  EmployeeDetailPage,
  CrewsPage,
  CrewDetailPage,
  PlanningPage,
  PlanningDetailPage,
  LoadCalcsPage,
  LoadCalcDetailPage,
  NemAmendmentsPage,
  NemAmendmentDetailPage,
  MessagesPage,
  NotificationsPage,
  TermsPage,
  PrivacyPage,
  AboutPage,
  ApplicationHistoryPage,
  LightweightCadPage,
  SolarDesignPage,
  TradeshowsPage,
  TradeshowDetailPage,
  PdfTestPage,
} from './lazyPages';

/**
 * Loading fallback shown while lazy components load.
 */
export function RouteFallback() {
  return (
    <div className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground">
      Loading workspace…
    </div>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Restricted Access</h1>
          <p className="text-sm text-muted-foreground">
            Admin privileges are required to open this workspace.
          </p>
        </header>
      </div>
    );
  }
  return <>{children}</>;
}

function adminElement(path: string, element: React.ReactNode): React.ReactNode {
  if (!isAdminRoutePath(path)) {
    return element;
  }
  return <AdminRoute>{element}</AdminRoute>;
}

/**
 * Mobile page components loader.
 * Returns memoized lazy components for mobile pages when mobile is active.
 */
function useMobilePages(isMobileActive: boolean) {
  const MobileDashboardPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Home/mobile/MobileHomePage')) : null),
    [isMobileActive]
  );
  const MobileEstimatesPage = useMemo(() => isMobileActive ? React.lazy(() => import('../pages/Estimates/mobile/MobileEstimatesPage')) : null, [isMobileActive]);
  const MobileEstimateDetailPage = useMemo(() => isMobileActive ? React.lazy(() => import('../pages/Estimates/mobile/MobileEstimateDetailPage')) : null, [isMobileActive]);
  const MobileDealsPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Deals/mobile/MobileDealsPage')) : null),
    [isMobileActive]
  );
  const MobileDealDetailPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Deals/mobile/MobileDealDetailPage')) : null),
    [isMobileActive]
  );
  const MobileProjectsPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Projects/mobile/MobileProjectsPage')) : null),
    [isMobileActive]
  );
  const MobileServicePage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/ServiceMaintenance/mobile/MobileServicePage')) : null),
    [isMobileActive]
  );
  const MobileTasksPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Tasks/mobile/MobileTasksPage')) : null),
    [isMobileActive]
  );
  const MobileTaskDetailPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Tasks/mobile/MobileTaskDetailPage')) : null),
    [isMobileActive]
  );
  const MobileTradeshowsPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Tradeshows/mobile/MobileTradeshowsPage')) : null),
    [isMobileActive]
  );
  const MobileCalendarPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Calendar/mobile/MobileCalendarPage')) : null),
    [isMobileActive]
  );
  const MobileActivitiesPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Activities/mobile/MobileActivitiesPage')) : null),
    [isMobileActive]
  );
  const MobileProjectDetailPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Projects/mobile/MobileProjectDetailPage')) : null),
    [isMobileActive]
  );
  const MobileServiceOrderDetailPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/ServiceMaintenance/mobile/MobileServiceOrderDetailPage')) : null),
    [isMobileActive]
  );
  const MobileTradeshowDetailPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Tradeshows/mobile/MobileTradeshowDetailPage')) : null),
    [isMobileActive]
  );
  const MobileContactsPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Contacts/mobile/MobileContactsPage')) : null),
    [isMobileActive]
  );
  const MobileContactDetailPage = useMemo(
    () => (isMobileActive ? React.lazy(() => import('../pages/Contacts/mobile/MobileContactDetailPage')) : null),
    [isMobileActive]
  );

  return {
    MobileDashboardPage,
    MobileEstimatesPage,
    MobileEstimateDetailPage,
    MobileDealsPage,
    MobileDealDetailPage,
    MobileProjectsPage,
    MobileServicePage,
    MobileTasksPage,
    MobileTaskDetailPage,
    MobileTradeshowsPage,
    MobileCalendarPage,
    MobileActivitiesPage,
    MobileProjectDetailPage,
    MobileServiceOrderDetailPage,
    MobileTradeshowDetailPage,
    MobileContactsPage,
    MobileContactDetailPage,
  };
}

/**
 * Contact detail route wrapper that handles navigation.
 */
function ContactDetailRoute() {
  const { contactId } = useParams<{ contactId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  if (!contactId) {
    return <Navigate to="/contacts" replace />;
  }

  return (
    <ContactDetailPage
      contactId={contactId}
      onClose={() => {
        const hasOrigin = (location.state as { fromDealId?: string; fromProjectId?: string } | null)?.fromDealId || 
                          (location.state as { fromDealId?: string; fromProjectId?: string } | null)?.fromProjectId;
        if (hasOrigin) {
          navigate(-1);
        } else {
          navigate('/contacts');
        }
      }}
    />
  );
}

/**
 * Main application content routes.
 * Renders all authenticated routes with mobile support.
 */
export function AppContentRoutes() {
  const location = useLocation();
  const isMobileActive = useIsMobileActive();
  
  const {
    MobileDashboardPage,
    MobileEstimatesPage,
    MobileEstimateDetailPage,
    MobileDealsPage,
    MobileDealDetailPage,
    MobileProjectsPage,
    MobileServicePage,
    MobileTasksPage,
    MobileTaskDetailPage,
    MobileTradeshowsPage,
    MobileCalendarPage,
    MobileActivitiesPage,
    MobileProjectDetailPage,
    MobileServiceOrderDetailPage,
    MobileTradeshowDetailPage,
    MobileContactsPage,
    MobileContactDetailPage,
  } = useMobilePages(isMobileActive);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes location={location}>
        {/* Dashboard */}
        <Route path="/" element={isMobileActive && MobileDashboardPage ? <MobileDashboardPage /> : <DashboardPage />} />

        {/* Deals */}
        <Route path="/deals" element={isMobileActive && MobileDealsPage ? <MobileDealsPage /> : <DealsPage />} />
        <Route path="/deals/my" element={isMobileActive && MobileDealsPage ? <MobileDealsPage /> : <DealsPage />} />
        <Route path="/deals/solar" element={isMobileActive && MobileDealsPage ? <MobileDealsPage /> : <DealsPage />} />
        <Route path="/deals/electrical" element={isMobileActive && MobileDealsPage ? <MobileDealsPage /> : <DealsPage />} />
        <Route path="/deals/service-maintenance" element={isMobileActive && MobileDealsPage ? <MobileDealsPage /> : <DealsPage />} />
        <Route path="/deals/remove-and-reinstall" element={isMobileActive && MobileDealsPage ? <MobileDealsPage /> : <DealsPage />} />
        <Route path="/deals/new" element={<DealDetailPage />} />
        <Route path="/deals/:dealId" element={isMobileActive && MobileDealDetailPage ? <MobileDealDetailPage /> : <DealDetailPage />} />
        <Route path="/deals/:dealId/web-proposal" element={<WebProposalPreviewPage />} />

        {/* Projects */}
        <Route path="/projects" element={isMobileActive && MobileProjectsPage ? <MobileProjectsPage /> : <ProjectsPage />} />
        <Route path="/projects/solar" element={isMobileActive && MobileProjectsPage ? <MobileProjectsPage /> : <ProjectsPage />} />
        <Route path="/projects/electrical" element={isMobileActive && MobileProjectsPage ? <MobileProjectsPage /> : <ProjectsPage />} />
        <Route path="/projects/service-maintenance" element={isMobileActive && MobileProjectsPage ? <MobileProjectsPage /> : <ProjectsPage />} />
        <Route path="/projects/remove-and-reinstall" element={isMobileActive && MobileProjectsPage ? <MobileProjectsPage /> : <ProjectsPage />} />
        <Route path="/projects/new" element={<ProjectCreatePage />} />
        <Route path="/projects/:projectId" element={isMobileActive && MobileProjectDetailPage ? <MobileProjectDetailPage /> : <ProjectDetailPage />} />
        <Route path="/projects/heco" element={<HecoPage />} />
        <Route path="/projects/designs" element={<DesignsPage />} />
        <Route path="/projects/permits" element={<PermitsPage />} />

        {/* Activities */}
        <Route path="/activities" element={isMobileActive && MobileActivitiesPage ? <MobileActivitiesPage /> : <ActivitiesPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:reportId" element={<ReportDetailPage />} />

        {/* Service & Maintenance */}
        <Route path="/service-maintenance" element={isMobileActive && MobileServicePage ? <MobileServicePage /> : <ServiceMaintenancePage />} />
        <Route path="/service-maintenance/service" element={isMobileActive && MobileServicePage ? <MobileServicePage /> : <ServiceMaintenancePage />} />
        <Route path="/service-maintenance/maintenance" element={isMobileActive && MobileServicePage ? <MobileServicePage /> : <ServiceMaintenancePage />} />
        <Route path="/service-maintenance/billing" element={<ServiceMaintenancePage />} />
        <Route path="/service-maintenance/:serviceOrderId" element={isMobileActive && MobileServiceOrderDetailPage ? <MobileServiceOrderDetailPage /> : <ServiceOrderDetailPage />} />

        {/* Contacts */}
        <Route path="/contacts" element={isMobileActive && MobileContactsPage ? <MobileContactsPage /> : <ContactsPage />} />
        <Route path="/contacts/new" element={<ContactsPage />} />
        <Route path="/contacts/:contactId" element={isMobileActive && MobileContactDetailPage ? <MobileContactDetailPage /> : <ContactDetailRoute />} />

        {/* Tasks */}
        <Route path="/tasks" element={isMobileActive && MobileTasksPage ? <MobileTasksPage /> : <TasksPage />} />
        <Route path="/tasks/:taskId" element={isMobileActive && MobileTaskDetailPage ? <MobileTaskDetailPage /> : <TaskDetailPage />} />

        {/* Calendar */}
        <Route path="/calendar" element={isMobileActive && MobileCalendarPage ? <MobileCalendarPage /> : <CalendarPage />} />
        <Route path="/calendar/:eventId" element={<CalendarEventDetailPage />} />

        {/* Employees & Crews */}
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/employees/:employeeId" element={<EmployeeDetailPage />} />
        <Route path="/crews" element={<CrewsPage />} />
        <Route path="/crews/:crewId" element={<CrewDetailPage />} />

        {/* Planning */}
        <Route path="/planning" element={adminElement('/planning', <PlanningPage />)} />
        <Route path="/planning/new" element={<PlanningDetailPage />} />
        <Route path="/planning/:planningId" element={adminElement('/planning/:planningId', <PlanningDetailPage />)} />

        {/* Load Calcs */}
        <Route path="/load-calcs" element={<LoadCalcsPage />} />
        <Route path="/load-calcs/new" element={<LoadCalcDetailPage />} />
        <Route path="/load-calcs/:loadCalcId" element={<LoadCalcDetailPage />} />

        {/* NEM Amendments */}
        <Route path="/nem-amendments" element={<NemAmendmentsPage />} />
        <Route path="/nem-amendments/new" element={<NemAmendmentDetailPage />} />
        <Route path="/nem-amendments/:nemAmendmentId" element={<NemAmendmentDetailPage />} />

        {/* Tradeshows */}
        <Route path="/tradeshows" element={isMobileActive && MobileTradeshowsPage ? <MobileTradeshowsPage /> : <TradeshowsPage />} />
        <Route path="/tradeshows/new" element={<TradeshowDetailPage mode="create" />} />
        <Route path="/tradeshows/:projectId" element={isMobileActive && MobileTradeshowDetailPage ? <MobileTradeshowDetailPage /> : <TradeshowDetailPage mode="edit" />} />

        {/* Messages */}
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/messages/:conversationId" element={<MessagesPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />

        {/* Estimates */}
        <Route path="/estimates" element={isMobileActive && MobileEstimatesPage ? <MobileEstimatesPage /> : <EstimatesHomeRoute />} />
        <Route path="/estimates/:estimateId" element={isMobileActive && MobileEstimateDetailPage ? <MobileEstimateDetailPage /> : <EstimateDetailRoute />} />

        {/* SOPs */}
        <Route path="/sops" element={adminElement('/sops', <SOPsPage />)} />
        <Route path="/sops/new" element={<SOPDetailPage />} />
        <Route path="/sops/:sopId" element={adminElement('/sops/:sopId', <SOPDetailPage />)} />

        {/* Redirects */}
        <Route path="/connections" element={<Navigate to="/dev" replace />} />
        <Route path="/database" element={<Navigate to="/dev?tab=database" replace />} />

        {/* Procurement routes */}
        <Route path="/procurement" element={<ProcurementIndexRedirect />} />
        <Route path="/procurement/items/new" element={<ItemCreateRoute />} />
        <Route path="/procurement/items/:itemId/duplicate" element={<ItemDuplicateRoute />} />
        <Route path="/procurement/kits/new" element={<KitCreateRoute />} />
        <Route path="/procurement/boms/new" element={<BomCreateRoute />} />
        <Route path="/procurement/purchase-orders/new" element={<PurchaseOrderCreateRoute />} />
        <Route path="/procurement/purchase-requisitions/new" element={<PurchaseRequisitionCreateRoute />} />
        <Route path="/procurement/items" element={<ProcurementPage />} />
        <Route path="/procurement/kits" element={<ProcurementPage />} />
        <Route path="/procurement/vendors" element={<ProcurementPage />} />
        <Route path="/procurement/boms" element={<ProcurementPage />} />
        <Route path="/procurement/purchase-requisitions" element={<ProcurementPage />} />
        <Route path="/procurement/purchase-orders" element={<ProcurementPage />} />
        <Route path="/procurement/inventory" element={<ProcurementPage />} />
        <Route path="/procurement/inventory-pulls" element={<ProcurementPage />} />
        <Route path="/procurement/items/:itemId" element={<ItemEditRoute />} />
        <Route path="/procurement/kits/:kitId" element={<KitEditRoute />} />
        <Route path="/procurement/boms/:bomId" element={<BomEditRoute />} />
        <Route path="/procurement/purchase-orders/:poId" element={<PurchaseOrderEditRoute />} />
        <Route path="/procurement/purchase-requisitions/:prId" element={<PurchaseRequisitionEditRoute />} />
        <Route path="/items" element={<Navigate to="/procurement/items" replace />} />

        {/* Admin & Dev */}
        <Route path="/users" element={adminElement('/users', <UsersPage />)} />
        <Route path="/quickbooks/*" element={adminElement('/quickbooks/*', <QuickBooksPage />)} />
        <Route path="/payroll" element={adminElement('/payroll', <PayrollPage />)} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/expenses" element={<Navigate to="/quickbooks" replace />} />
        <Route path="/dev" element={adminElement('/dev', <DevPage />)} />
        <Route path="/decisions" element={adminElement('/decisions', <DecisionCenterPage />)} />
        <Route path="/decisions/:decisionId" element={adminElement('/decisions/:decisionId', <DecisionCenterPage />)} />
        <Route path="/board-advisor" element={<Navigate to="/decisions" replace />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/files" element={<FilesPage />} />

        {/* Info & Legal */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/history" element={<ApplicationHistoryPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Misc */}
        <Route path="/solar-design" element={adminElement('/solar-design', <SolarDesignPage />)} />
        <Route path="/solar-design/:solarDesignId" element={adminElement('/solar-design/:solarDesignId', <SolarDesignPage />)} />
        <Route path="/cad" element={<LightweightCadPage />} />
        <Route path="/enphase" element={adminElement('/enphase', <EnphasePage />)} />
        <Route path="/google-ads-analytics" element={<GoogleAdsAnalyticsPage />} />
        <Route path="/playground" element={<PlaygroundPage />} />
        <Route path="/pdf-test" element={<PdfTestPage />} />
        <Route path="/guides" element={<GuidesPage />} />

        {/* Tools */}
        <Route path="/tools" element={<ToolsLayout />}>
          <Route index element={<UsageAllocator />} />
          <Route path="usage-allocator" element={<UsageAllocator />} />
          <Route path="api-tester" element={<ApiTester />} />
          <Route path="wire-utility" element={<WireUtilityTool />} />
          <Route path="wire-specs" element={<WireSpecsTool />} />
          <Route path="conduit-fill" element={<ConduitFillTool />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
